import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  HStack,
  VStack,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Badge,
  Divider,
  Tag,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { aiAPI, systemAPI, userAPI } from "../../services";
import { PrivateNavbar } from "../../components/layout/PrivateNavbar";
import { Footer } from "../../components/layout/Footer";

interface TokenUsage {
  id: string;
  model: string;
  promptTokens: number;
  completionTokens: number;
  inputCost: number;
  outputCost: number;
  createdAt: string;
  updatedAt: string;
}

interface ModelUsage {
  promptTokens: number;
  completionTokens: number;
  inputCost: number;
  outputCost: number;
}

interface UsageResponse {
  subscription: {
    id: string;
    plan: string;
    billingPeriod: string | null;
    status: string;
    credits: string;
    totalSpent: string;
    monthlyFee: string | null;
    nextBillingDate: string | null;
  };
  usage: {
    total: {
      tokens: number;
      cost: string;
    };
    byModel: {
      [key: string]: ModelUsage;
    };
    recentUsage: TokenUsage[];
  };
}

export const Usage = () => {
  const { t, i18n } = useTranslation();
  const showBrlPrice = i18n.language === "pt-BR";
  const [usageData, setUsageData] = useState<UsageResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [currencyPairs, setCurrencyPairs] = useState<any>({ usd_brl: 7 });
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const usageRef = useRef<HTMLDivElement>(null);

  const handlePaginate = async (direction: "backward" | "forward") => {
    if (direction === "backward") {
      setPage(Math.max(1, page - 1));
    }
    if (direction === "forward") {
      setPage(Math.min(totalPages, page + 1));
    }
    // TODO: needs proper fix, undesired scroll to top is happening
    setTimeout(async () => {
      // scroll to
      if (usageRef.current) {
        usageRef.current.scrollIntoView();
      }
    }, 300);
  };

  const loadUsage = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await userAPI.getTokenUsage(page, limit);

      setUsageData(response);
      setTotalPages(response.usage.recentUsagePagination.pages);
      const currencyPairs = await systemAPI.getCurrencyPairs();
      setCurrencyPairs(currencyPairs);
    } catch (err) {
      setError(t("usage.error"));
      console.error("Error loading usage data:", err);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    loadUsage();
  }, [t, page]);

  const formatNumber = (num: number | string) => {
    return Number(num).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 10,
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(
      i18n.language === "pt-BR" ? "pt-BR" : "en-US"
    );
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PrivateNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <VStack spacing={4} textAlign="center">
              <Heading
                size="2xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("usage.title")}
              </Heading>
              <Text color="gray.500">{t("usage.subtitle")}</Text>
              {showBrlPrice && (
                <Tag size="md" colorScheme="blue" ml={2}>
                  USD/BRL: {currencyPairs.usd_brl}
                </Tag>
              )}
            </VStack>

            {error && (
              <Alert status="error" mb={4}>
                <AlertIcon />
                {error}
              </Alert>
            )}

            {isLoading ? (
              <Box textAlign="center" py={8}>
                <Spinner />
                <Text mt={2}>{t("usage.loading")}</Text>
              </Box>
            ) : usageData ? (
              <VStack spacing={6}>
                {/* Subscription Stats */}
                <Box bg={cardBg} p={8} borderRadius="xl" shadow="md" w="full">
                  <StatGroup>
                    <Stat>
                      <StatLabel>
                        {t("usage.stats.subscription.plan")}
                      </StatLabel>
                      <StatNumber>
                        <Tag colorScheme="green" fontSize="lg" p={2}>
                          {usageData.subscription.plan}
                        </Tag>
                      </StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>
                        {t("usage.stats.subscription.credits")}
                      </StatLabel>
                      <StatNumber>
                        {/* <Tag size="lg" colorScheme="blue" ml={2}> */}$
                        {formatNumber(usageData.subscription.credits)}
                        {/* </Tag> */}
                      </StatNumber>
                      {showBrlPrice && (
                        <>
                          R${" "}
                          {formatNumber(
                            Number(usageData.subscription.credits) *
                              currencyPairs.usd_brl
                          )}
                        </>
                      )}
                    </Stat>
                    <Stat>
                      <StatLabel>
                        {t("usage.stats.subscription.totalSpent")}
                      </StatLabel>
                      <StatNumber>
                        ${formatNumber(usageData.subscription.totalSpent)}
                      </StatNumber>
                      {showBrlPrice && (
                        <>
                          R${" "}
                          {formatNumber(
                            Number(usageData.subscription.totalSpent) *
                              currencyPairs.usd_brl
                          )}
                        </>
                      )}
                    </Stat>
                  </StatGroup>
                </Box>

                {/* Total Usage Stats */}
                <Box bg={cardBg} p={8} borderRadius="xl" shadow="md" w="full">
                  <Heading size="md" mb={4}>
                    {t("usage.stats.total.title")}
                  </Heading>
                  <StatGroup>
                    <Stat>
                      <StatLabel>{t("usage.stats.total.tokens")}</StatLabel>
                      <StatNumber>
                        {usageData.usage.total.tokens.toLocaleString()}
                      </StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>{t("usage.stats.total.cost")}</StatLabel>
                      <StatNumber>
                        ${formatNumber(usageData.usage.total.cost)}
                      </StatNumber>
                      {showBrlPrice && (
                        <>
                          R${" "}
                          {formatNumber(
                            Number(usageData.usage.total.cost) *
                              currencyPairs.usd_brl
                          )}
                        </>
                      )}
                    </Stat>
                  </StatGroup>
                </Box>

                {/* Usage by Model */}
                <Box bg={cardBg} p={8} borderRadius="xl" shadow="md" w="full">
                  <Heading size="md" mb={4}>
                    {t("usage.stats.byModel.title")}
                  </Heading>
                  <Box overflowX="auto">
                    <Table
                      variant="simple"
                      borderWidth={1}
                      borderColor={borderColor}
                    >
                      <Thead>
                        <Tr>
                          <Th>{t("usage.stats.byModel.model")}</Th>
                          <Th isNumeric>
                            {t("usage.stats.byModel.inputOutputTokens")}
                          </Th>
                          <Th isNumeric>
                            {t("usage.stats.byModel.inputCost")}
                          </Th>
                          <Th isNumeric>
                            {t("usage.stats.byModel.outputCost")}
                          </Th>
                          <Th isNumeric>
                            {t("usage.stats.byModel.totalCost")}
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {Object.entries(usageData.usage.byModel).map(
                          ([model, usage]) => (
                            <Tr key={model} _hover={{ bg: hoverBg }}>
                              <Td>{model}</Td>
                              <Td isNumeric>
                                {usage.promptTokens.toLocaleString()} /{" "}
                                {usage.completionTokens.toLocaleString()}
                              </Td>
                              <Td isNumeric>
                                ${formatNumber(usage.inputCost)}
                              </Td>
                              <Td isNumeric>
                                ${formatNumber(usage.outputCost)}
                              </Td>
                              <Td isNumeric>
                                $
                                {formatNumber(
                                  Number(usage.inputCost) +
                                    Number(usage.outputCost)
                                )}
                              </Td>
                            </Tr>
                          )
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>

                {/* Recent Usage */}
                <Box bg={cardBg} p={8} borderRadius="xl" shadow="md" w="full">
                  <Heading size="md" mb={4}>
                    {t("usage.stats.recent.title")}
                  </Heading>
                  <Box overflowX="auto">
                    <div ref={usageRef} />

                    {usageData.usage.recentUsage.length > 0 ? (
                      <Table
                        variant="simple"
                        borderWidth={1}
                        borderColor={borderColor}
                      >
                        <Thead>
                          <Tr>
                            <Th>{t("usage.table.date")}</Th>
                            <Th>{t("usage.table.model")}</Th>
                            <Th isNumeric>
                              {t("usage.stats.byModel.inputOutputTokens")}
                            </Th>
                            <Th isNumeric>{t("usage.table.inputCost")}</Th>
                            <Th isNumeric>{t("usage.table.outputCost")}</Th>
                            <Th isNumeric>{t("usage.table.totalCost")}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {usageData.usage.recentUsage.map((entry) => (
                            <Tr key={entry.id} _hover={{ bg: hoverBg }}>
                              <Td>{formatDate(entry.createdAt)}</Td>
                              <Td>{entry.model}</Td>
                              <Td isNumeric>
                                {entry.promptTokens.toLocaleString()} /{" "}
                                {entry.completionTokens.toLocaleString()}
                              </Td>
                              <Td isNumeric>
                                ${formatNumber(entry.inputCost)}
                              </Td>
                              <Td isNumeric>
                                ${formatNumber(entry.outputCost)}
                              </Td>
                              <Td isNumeric>
                                $
                                {formatNumber(
                                  Number(entry.inputCost) +
                                    Number(entry.outputCost)
                                )}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    ) : (
                      <Text textAlign="center" py={4}>
                        {t("usage.stats.recent.noTransactions")}
                      </Text>
                    )}
                  </Box>
                  <HStack spacing={2} justify="space-between" w="full">
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handlePaginate("backward");
                      }}
                      isDisabled={page === 1}
                    >
                      {t("common.previous")}
                    </Button>
                    <Text>
                      {t("common.page")} {page} / {totalPages}
                    </Text>
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handlePaginate("forward");
                      }}
                      isDisabled={page === totalPages}
                    >
                      {t("common.next")}
                    </Button>
                  </HStack>
                </Box>
              </VStack>
            ) : (
              <Box textAlign="center" py={8}>
                <Text>{t("usage.noData")}</Text>
              </Box>
            )}
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};
